/**
 * Richard & Anthony — Theme Core
 * Loaded on every page with defer via layout/theme.liquid.
 * Provides the global RA namespace, Cart API wrapper, money formatting,
 * and a delegated add-to-cart click handler.
 */

window.RA = window.RA || {};

/* --------------------------------------------------------------------------
   Cart API wrapper
   All methods return Promises. Every mutation dispatches 'cart:updated'
   on document so any component (cart drawer, header badge, etc.) can react.
   -------------------------------------------------------------------------- */
RA.cart = {
  /**
   * Fetch the current cart state.
   * @returns {Promise<Object>} Shopify cart JSON
   */
  async get() {
    const response = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  },

  /**
   * Add a variant to the cart.
   * @param {number} variantId
   * @param {number} [quantity=1]
   * @returns {Promise<Object>} The items that were added
   */
  async add(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
    });
    if (!response.ok) throw new Error('Add to cart failed');
    const data = await response.json();
    /* add.js does not return the full cart, so we fetch it separately */
    const cart = await RA.cart.get();
    RA.cart.dispatchUpdate(cart);
    return data;
  },

  /**
   * Bulk-update quantities by variant id.
   * @param {Object} updates — e.g. { 12345678: 2, 87654321: 0 }
   * @returns {Promise<Object>} Updated cart JSON
   */
  async update(updates) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    const cart = await response.json();
    RA.cart.dispatchUpdate(cart);
    return cart;
  },

  /**
   * Change a single line item's quantity (1-based line index).
   * Setting quantity to 0 removes the item.
   * @param {number} line  — 1-based line index
   * @param {number} quantity
   * @returns {Promise<Object>} Updated cart JSON
   */
  async change(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error('Cart change failed');
    const cart = await response.json();
    RA.cart.dispatchUpdate(cart);
    return cart;
  },

  /**
   * Dispatch the global cart:updated CustomEvent.
   * @param {Object} cart — full Shopify cart JSON
   */
  dispatchUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: { cart } })
    );
  },
};

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

/**
 * Format a Shopify price (in cents) as a Euro string.
 * @param {number} cents
 * @returns {string} e.g. "€ 229,00"
 */
RA.formatMoney = function (cents) {
  return '€ ' + (cents / 100).toFixed(2).replace('.', ',');
};

/**
 * Dispatch the cart:open event so the cart drawer opens.
 */
RA.openCart = function () {
  document.dispatchEvent(new CustomEvent('cart:open'));
};

/* --------------------------------------------------------------------------
   Global delegated add-to-cart handler
   Targets any button / element with [data-add-to-cart].
   Required data attributes on the element:
     data-variant-id="<id>"
   Optional:
     data-adding-text="..."   (shown while request is in flight)
     data-added-text="..."    (shown briefly after success)
   -------------------------------------------------------------------------- */
document.addEventListener('click', async function (e) {
  var btn = e.target.closest('[data-add-to-cart]');
  if (!btn) return;
  e.preventDefault();

  var variantId = parseInt(btn.dataset.variantId, 10);
  if (!variantId) return;

  var originalText = btn.textContent;
  btn.textContent = btn.dataset.addingText || 'Adding...';
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');

  try {
    await RA.cart.add(variantId);
    btn.textContent = btn.dataset.addedText || 'Added!';
    RA.openCart();
    setTimeout(function () {
      btn.textContent = originalText;
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }, 2000);
  } catch (error) {
    console.error('Add to cart error:', error);
    btn.textContent = originalText;
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
  }
});
